/**
 * SubscriptionsPage — Central Dispatch Event API subscription manager.
 *
 * - List subscriptions stored in our DB
 * - Create a new CD subscription (registers our webhook with CD)
 * - View subscription details (local + live CD detail)
 * - Update subscription (event types, callback URL)
 * - Renew Api-Key (rotate compromised key)
 * - "Get Mine" — fetch all subscriptions from CD for this account
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Rss, Plus, RefreshCw, X, Eye, Edit2, RotateCcw,
  ChevronDown, ChevronUp, Webhook, CheckCircle2, XCircle,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CD_EVENT_TYPES_DEFAULT = [
  'DispatchCreated', 'DispatchUpdated', 'DispatchAccepted',
  'DispatchVehiclePickedup', 'DispatchVehicleDelivered', 'DispatchCancelled',
];

// ── Create form ───────────────────────────────────────────────────────────────

function CreateForm({ availableTypes, onSave, onCancel, loading }) {
  const [form, setForm] = useState({
    callback_url: '',
    marketplaces: ['40177'],
    event_types: ['DispatchCreated', 'DispatchUpdated', 'DispatchAccepted', 'DispatchVehiclePickedup', 'DispatchVehicleDelivered'],
  });

  const toggleType = (et) =>
    setForm(f => ({
      ...f,
      event_types: f.event_types.includes(et)
        ? f.event_types.filter(e => e !== et)
        : [...f.event_types, et],
    }));

  const marketplaceStr = form.marketplaces.join(', ');

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 flex gap-2 text-xs text-blue-300">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          Leave callback URL blank to use the <code className="font-mono">CD_EVENTS_WEBHOOK_URL</code> env var.
          The Api-Key returned by CD will be stored securely to validate incoming events.
        </span>
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Callback URL (our webhook endpoint)</label>
        <Input
          value={form.callback_url}
          onChange={e => setForm(f => ({ ...f, callback_url: e.target.value }))}
          placeholder="https://your-domain.com/api/webhook/cd-events (leave blank = env default)"
          className="bg-transparent border-border font-mono text-xs"
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Marketplace IDs (comma-separated)</label>
        <Input
          value={marketplaceStr}
          onChange={e => setForm(f => ({ ...f, marketplaces: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
          placeholder="40177"
          className="bg-transparent border-border"
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-2 block">
          Subscribe to event types ({form.event_types.length} selected)
        </label>
        <div className="flex flex-wrap gap-2">
          {availableTypes.map(et => (
            <button
              key={et}
              onClick={() => toggleType(et)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                form.event_types.includes(et)
                  ? 'bg-accent text-accent-foreground border-accent'
                  : 'border-white/10 text-muted-foreground hover:border-white/30'
              }`}
            >
              {et}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
        <Button variant="outline" onClick={onCancel} className="border-white/10">Cancel</Button>
        <Button
          onClick={() => onSave(form)}
          disabled={loading || form.event_types.length === 0}
          className="bg-accent hover:bg-accent/90"
        >
          {loading ? 'Creating…' : 'Create Subscription'}
        </Button>
      </div>
    </div>
  );
}

// ── Edit form ─────────────────────────────────────────────────────────────────

function EditForm({ sub, availableTypes, onSave, onCancel, loading }) {
  const [form, setForm] = useState({
    callback_url: sub.callback_url || '',
    marketplaces: sub.marketplaces || [],
    event_types:  sub.event_types || [],
    is_enabled:   sub.is_enabled ?? true,
  });

  const toggleType = (et) =>
    setForm(f => ({
      ...f,
      event_types: f.event_types.includes(et)
        ? f.event_types.filter(e => e !== et)
        : [...f.event_types, et],
    }));

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Callback URL *</label>
        <Input
          value={form.callback_url}
          onChange={e => setForm(f => ({ ...f, callback_url: e.target.value }))}
          className="bg-transparent border-border font-mono text-xs"
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Marketplace IDs</label>
        <Input
          value={form.marketplaces.join(', ')}
          onChange={e => setForm(f => ({ ...f, marketplaces: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
          className="bg-transparent border-border"
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-2 block">Event Types</label>
        <div className="flex flex-wrap gap-2">
          {availableTypes.map(et => (
            <button key={et} onClick={() => toggleType(et)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                form.event_types.includes(et)
                  ? 'bg-accent text-accent-foreground border-accent'
                  : 'border-white/10 text-muted-foreground hover:border-white/30'
              }`}
            >
              {et}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <label className="text-xs text-muted-foreground">Enabled</label>
        <button
          onClick={() => setForm(f => ({ ...f, is_enabled: !f.is_enabled }))}
          className={`relative w-10 h-5 rounded-full transition-colors ${form.is_enabled ? 'bg-accent' : 'bg-muted'}`}
        >
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${form.is_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
        <Button variant="outline" onClick={onCancel} className="border-white/10">Cancel</Button>
        <Button
          onClick={() => onSave(form)}
          disabled={loading || !form.callback_url || form.event_types.length === 0}
          className="bg-accent hover:bg-accent/90"
        >
          {loading ? 'Updating…' : 'Update Subscription'}
        </Button>
      </div>
    </div>
  );
}

// ── Detail panel ──────────────────────────────────────────────────────────────

function SubscriptionDetail({ sub, availableTypes, onClose, onUpdated }) {
  const { token } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [detail, setDetail]   = useState(sub);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => { setDetail(sub); setEditing(false); }, [sub]);

  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cd-events/subscriptions/${sub.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Update failed');
      const updated = await res.json();
      setDetail(updated);
      setEditing(false);
      toast.success('Subscription updated on CD');
      onUpdated(updated);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleRenew = async () => {
    if (!window.confirm('Rotate the Api-Key? The old key will be immediately invalidated.')) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cd-events/subscriptions/${sub.id}/renew`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Renew failed');
      toast.success('Api-Key rotated successfully');
      onUpdated({ ...detail });
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const cdDetail = detail.cd_detail;

  return (
    <Card className="bg-card border-white/10">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Webhook className="h-4 w-4 text-accent" />
              Subscription Detail
            </CardTitle>
            {detail.cd_subscription_id && (
              <p className="text-xs text-muted-foreground font-mono mt-0.5">
                CD ID: {detail.cd_subscription_id}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`text-[11px] ${
                detail.is_enabled
                  ? 'bg-green-500/10 text-green-400 border-green-500/20'
                  : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
              }`}
            >
              {detail.is_enabled ? 'Enabled' : 'Disabled'}
            </Badge>
            <button onClick={onClose} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {editing ? (
          <EditForm sub={detail} availableTypes={availableTypes} onSave={handleUpdate} onCancel={() => setEditing(false)} loading={saving} />
        ) : (
          <>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Callback URL</p>
                <p className="text-xs font-mono mt-0.5 break-all text-foreground">{detail.callback_url}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Marketplaces</p>
                <p className="text-sm mt-0.5">{(detail.marketplaces || []).join(', ') || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Subscribed Events</p>
                <div className="flex flex-wrap gap-1.5">
                  {(detail.event_types || []).map(et => (
                    <Badge key={et} variant="outline" className="text-[11px] bg-accent/10 text-accent border-accent/20">{et}</Badge>
                  ))}
                </div>
              </div>

              {cdDetail && (
                <div className="rounded-lg border border-white/5 p-3 space-y-2">
                  <p className="text-xs text-muted-foreground font-medium">Live CD Status</p>
                  <div className="flex items-center gap-2">
                    {cdDetail.isEnabled
                      ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                      : <XCircle className="h-4 w-4 text-red-400" />
                    }
                    <span className="text-xs">{cdDetail.isEnabled ? 'Active on CD' : 'Disabled on CD'}</span>
                  </div>
                </div>
              )}

              {(detail.recent_events || []).length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Recent Received Events</p>
                  <div className="space-y-1">
                    {detail.recent_events.slice(0, 5).map(ev => (
                      <div key={ev.id} className="flex items-center justify-between text-xs py-1 px-2 rounded bg-muted/20">
                        <span className="font-mono text-muted-foreground">{ev.event_type}</span>
                        <div className="flex items-center gap-2">
                          {ev.load_id && (
                            <span className="text-accent text-[10px]">Load matched</span>
                          )}
                          <span className="text-muted-foreground">{new Date(ev.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
              <Button size="sm" variant="outline" className="border-white/10 gap-1.5" onClick={() => setEditing(true)} disabled={saving}>
                <Edit2 className="h-3.5 w-3.5" /> Edit
              </Button>
              <Button size="sm" variant="outline" className="border-white/10 gap-1.5" onClick={handleRenew} disabled={saving}>
                <RotateCcw className="h-3.5 w-3.5" /> Rotate Key
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SubscriptionsPage() {
  const { token } = useAuth();
  const [subs, setSubs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [creating, setCreating]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [availableTypes, setAvailableTypes] = useState(CD_EVENT_TYPES_DEFAULT);
  const [cdMine, setCdMine]         = useState(null);
  const [fetchingMine, setFetchingMine] = useState(false);

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cd-events/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSubs(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cd-events/types`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const types = (data.items || []).map(i => i.eventType).filter(Boolean);
          if (types.length) setAvailableTypes(types);
        }
      } catch {}
    };
    loadTypes();
  }, [token]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cd-events/subscriptions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Create failed');
      toast.success('CD subscription created — webhook registered with Central Dispatch');
      setCreating(false);
      fetchSubs();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleViewDetail = async (sub) => {
    try {
      const res = await fetch(`${API_URL}/api/cd-events/subscriptions/${sub.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSelectedSub(await res.json());
    } catch { toast.error('Failed to load details'); }
  };

  const handleGetMine = async () => {
    setFetchingMine(true);
    setCdMine(null);
    try {
      const res = await fetch(`${API_URL}/api/cd-events/subscriptions/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      setCdMine(await res.json());
    } catch (e) { toast.error(e.message); }
    finally { setFetchingMine(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-cd-subscriptions">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-semibold flex items-center gap-2">
            <Rss className="h-6 w-6 text-accent" strokeWidth={1.5} />
            CD Event Subscriptions
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage Central Dispatch webhook subscriptions — register our endpoint to receive real-time dispatch events
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSubs}
            className="border-white/10 gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => { setCreating(true); setSelectedSub(null); }}
            className="bg-accent hover:bg-accent/90 gap-1.5"
          >
            <Plus className="h-4 w-4" /> New Subscription
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: 'Total Subscriptions', value: subs.length, color: 'text-foreground' },
          { label: 'Enabled', value: subs.filter(s => s.is_enabled).length, color: 'text-green-400' },
          { label: 'Disabled', value: subs.filter(s => !s.is_enabled).length, color: 'text-yellow-400' },
        ].map(s => (
          <div key={s.label} className="rounded-xl border border-white/5 bg-card p-4">
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className={`text-2xl font-semibold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Create form */}
      {creating && (
        <Card className="bg-card border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Plus className="h-4 w-4 text-accent" /> New CD Event Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CreateForm availableTypes={availableTypes} onSave={handleCreate} onCancel={() => setCreating(false)} loading={saving} />
          </CardContent>
        </Card>
      )}

      {/* Detail panel */}
      {selectedSub && (
        <SubscriptionDetail
          sub={selectedSub}
          availableTypes={availableTypes}
          onClose={() => setSelectedSub(null)}
          onUpdated={(updated) => {
            setSubs(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s));
            setSelectedSub(prev => ({ ...prev, ...updated }));
          }}
        />
      )}

      {/* Get mine from CD */}
      <Card className="bg-card border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Rss className="h-4 w-4 text-accent" /> Get My Subscriptions from CD
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <p className="text-xs text-muted-foreground flex-1">
              Fetch all subscriptions directly from Central Dispatch for this account's bearer token.
            </p>
            <Button
              variant="outline"
              onClick={handleGetMine}
              disabled={fetchingMine}
              className="border-white/10 gap-1.5 shrink-0"
            >
              {fetchingMine ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Rss className="h-4 w-4" />}
              Fetch from CD
            </Button>
          </div>
          {cdMine && (
            <div className="mt-4 space-y-2">
              <p className="text-xs text-muted-foreground">{cdMine.count ?? 0} subscription(s) on CD</p>
              {(cdMine.items || []).map(item => (
                <div key={item.subscriptionId} className="p-3 rounded-xl border border-white/5 bg-muted/10 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-muted-foreground">{item.subscriptionId}</span>
                    <Badge
                      variant="outline"
                      className={`text-[11px] ${item.isEnabled ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'}`}
                    >
                      {item.isEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <p className="text-xs font-mono break-all text-foreground">{item.callbackUrl}</p>
                  <div className="flex flex-wrap gap-1">
                    {(item.subscriptionRules?.eventTypes || []).map(et => (
                      <span key={et} className="text-[10px] px-1.5 py-0.5 rounded bg-accent/10 text-accent">{et}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscription list */}
      <Card className="bg-card border-white/5">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-20 text-muted-foreground">Loading…</div>
          ) : subs.length === 0 ? (
            <div className="text-center py-20">
              <Rss className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">No CD subscriptions yet</p>
              <p className="text-xs text-muted-foreground mt-1">Create a subscription to register our webhook with Central Dispatch</p>
              {!creating && (
                <Button onClick={() => setCreating(true)} className="mt-4 bg-accent hover:bg-accent/90 gap-1.5">
                  <Plus className="h-4 w-4" /> Create first subscription
                </Button>
              )}
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="divide-y divide-white/5">
                {subs.map(sub => (
                  <div
                    key={sub.id}
                    className="p-4 hover:bg-muted/20 transition-colors cursor-pointer"
                    onClick={() => handleViewDetail(sub)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`mt-0.5 h-2.5 w-2.5 rounded-full shrink-0 ${sub.is_enabled ? 'bg-green-500' : 'bg-yellow-500'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-mono text-muted-foreground">
                              {sub.cd_subscription_id
                                ? sub.cd_subscription_id.slice(0, 18) + '…'
                                : 'Pending CD ID'}
                            </span>
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${sub.is_enabled ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'}`}
                            >
                              {sub.is_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 break-all">{sub.callback_url}</p>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {(sub.event_types || []).slice(0, 4).map(et => (
                              <span key={et} className="text-[10px] px-1.5 py-0.5 rounded bg-accent/10 text-accent">{et}</span>
                            ))}
                            {(sub.event_types || []).length > 4 && (
                              <span className="text-[10px] text-muted-foreground">+{sub.event_types.length - 4}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-accent hover:bg-accent/10"
                          onClick={e => { e.stopPropagation(); handleViewDetail(sub); }}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" /> Details
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
